import {useEffect, useState} from 'react';
import { useCameraKit } from './hooks/useCameraKit';
import LensSelector from "./components/lens-selector/lens-selector";
import PoweredBy from "./components/powered-by/powered-by";
import SharingScreen from "./components/sharing-screen/sharing-screen";
import StartScreen from "./components/start-screen/start-screen";
import StartScreenV2 from "./components/start-screen-v2/start-screen-v2";
import { LOCALE } from "./locales";
import { track, TRACK } from "./libs/teads-tracking";
import LensTrackZones from "./components/lens-track-zones/lens-track-zones";
import logoImage from "./assets/miele-logo-box.webp";
import CaptureSwitcher from "./components/captupe-switcher/capture-switcher";
import './app-ui.css';
import {
    CAPTURE_METHOD, MAX_RECORD_DURATION, VIDEO_RECORDING_CONTENT_TYPE, START_RECORDING_PERCENT_FROM
} from "./constants";

// /v2 serves the second variant of the experience (Figma node 7137:932); it
// combines with the locale entry links, e.g. /ch-fr/v2 (see locales.js).
const IS_V2 = /\/v2\/?$/.test(window.location.pathname);
document.documentElement.lang = LOCALE.lang;

// The logo is a DOM element layered over the canvas, so the lens output does not
// contain it. Paint it into saved photos so they match what the user sees.
let topLogoImage = null;
function loadTopLogo() {
    if (topLogoImage) return Promise.resolve(topLogoImage);
    return new Promise((resolve, reject) => {
        const image = new Image();
        image.onload = () => { topLogoImage = image; resolve(image); };
        image.onerror = () => reject(new Error('Could not load the logo'));
        image.src = logoImage;
    });
}

async function drawTopLogo(context, width) {
    const logoElement = document.querySelector('.top-logo');
    if (!logoElement) return;

    // Measured off the live element, so safe-area insets and any CSS change to
    // the logo are reflected in the photo automatically.
    const rect = logoElement.getBoundingClientRect();
    if (!rect.width || !rect.height) return;

    const scale = width / window.innerWidth;
    const image = await loadTopLogo();
    context.drawImage(
        image,
        rect.left * scale, rect.top * scale,
        rect.width * scale, rect.height * scale
    );
}

function AppUI({ showDebug }) {
  const {
      isStarting, started, start, lenses, lensIndex, changeLens, toggleCamera, isApplyingLens,
      capturePhoto, createCaptureStream, stopCaptureRendering
  } = useCameraKit();

  const [ sharedBlob, setSharedBlob ] = useState(null);
  const [ width, setWidth ] = useState(`${window.innerWidth}px`);
  const [ height, setHeight ] = useState(`${window.innerHeight}px`);
  const [ captureMethod, setCaptureMethod ] = useState(CAPTURE_METHOD.PHOTO);
  const [ recording, setRecording ] = useState(false);
  const [ recordingPercents, setRecordingPercents ] = useState(START_RECORDING_PERCENT_FROM);
  const [ recordingTimer, setRecordingTimer ] = useState(0);
  const [ mediaRecorderRef, setMediaRecorderRef ] = useState(null);

  async function capture(e, cancel = false) {
      if (e.target.nodeName !== 'DIV' || !e.target.classList.contains('active-lens')) return;
      try {
          const liveRenderTarget = document.querySelector('.app-container canvas');
          const style = window.getComputedStyle(liveRenderTarget);
          showDebug(`PHOTO/VIDEO ${style.width}x${style.height}`);
          setWidth(style.width);
          setHeight(style.height);

          if (captureMethod === CAPTURE_METHOD.PHOTO) {
              track(TRACK.AR_PHOTO);
              capturePhoto("image/png", drawTopLogo)
                  .then(blob => setSharedBlob(blob))
                  .catch(error => {
                      console.error(error);
                      showDebug(error.message);
                  });
          }
          else if (captureMethod === CAPTURE_METHOD.VIDEO && !cancel) {
              clearInterval(recordingTimer);
              const intervalStep = 100;
              let duration = 0;
              if (!MediaRecorder.isTypeSupported(VIDEO_RECORDING_CONTENT_TYPE)) {
                  return showDebug(`Your device does not support ${VIDEO_RECORDING_CONTENT_TYPE} codec`);
              }
              const mediaStream = await createCaptureStream(30);
              if (!mediaStream) return showDebug('Could not start a capture stream');
              const mediaRecorder = new MediaRecorder(mediaStream, {
                  mimeType: VIDEO_RECORDING_CONTENT_TYPE
              });
              mediaRecorder.addEventListener('stop', () => stopCaptureRendering());
              mediaRecorder.addEventListener('dataavailable', e => {
                  if (!e.data.size) return showDebug('No recorded data available');
                  const blob = new Blob([e.data]);
                  showDebug(blob ? 'blob' : 'no blob');
                  setSharedBlob(blob);
              });
              mediaRecorder.start();
              setRecording(true);

              const recTimer = setInterval(() => {
                  if (duration > MAX_RECORD_DURATION) {
                      clearInterval(recTimer);
                      mediaRecorder.stop();
                      setRecording(false);
                      setRecordingPercents(START_RECORDING_PERCENT_FROM);
                      return;
                  }
                  setRecordingPercents(100 / MAX_RECORD_DURATION * duration);
                  duration += intervalStep;
              }, 100);

              setRecordingTimer(recTimer);
              setMediaRecorderRef(mediaRecorder);

          }
          else if (captureMethod === CAPTURE_METHOD.VIDEO && cancel) {
              clearInterval(recordingTimer);
              setRecordingPercents(START_RECORDING_PERCENT_FROM);
              mediaRecorderRef.stop();
              setRecording(false);
          }
      } catch (error) {
          console.error(error);
          showDebug(error.message);
      }
  }

  useEffect(() => {
      document.querySelectorAll('.capture-switcher-container .swiper-slide').forEach(el => {
          el.style.visibility = recording ? 'hidden' : 'visible';
      });
      document.querySelectorAll('.lenses-swiper-container .swiper-slide:not(.swiper-slide-active)').forEach(el => {
          el.style.visibility = recording ? 'hidden' : 'visible';
      });
  }, [ recording ]);

  const StartComponent = IS_V2 ? StartScreenV2 : StartScreen;

  return isStarting ? <StartComponent started={started} onStart={start} /> : (<>
      <div className="top-logo"></div>
      <LensTrackZones active={!sharedBlob && !recording} />
      {!IS_V2 && (
          <button onClick={() => {
                    // keepalive in track() lets the hit outlive the reload
                    track(TRACK.AR_BACK);
                    window.location.reload();
                  }}
                  className={'back-button' + (recording || sharedBlob ? ' hidden' : ' visible')}
                  aria-label="Back to start screen"></button>
      )}
      <button onClick={toggleCamera} className={'switcher-camera' + (recording ? ' hidden' : ' visible')}></button>

      {/* CaptureSwitcher component removed to hide it in all modes */}
      {/* <CaptureSwitcher captureMethod={captureMethod}
                       toggleCaptureMethod={setCaptureMethod}
      /> */}

      <LensSelector lenses={lenses}
                    lensIndex={lensIndex}
                    setLensIndex={changeLens}
                    shareContent={setSharedBlob}
                    captureMethod={captureMethod}
                    capture={capture}
                    recording={recording}
                    recordingPercents={recordingPercents}
      />
      <a href={LOCALE.shopUrl}
         target="_blank"
         rel="noopener noreferrer"
         onClick={() => track(TRACK.CTA)}
         className={'shop-now-button' + (recording || sharedBlob ? ' hidden' : ' visible')}>
          <img src={LOCALE.strings.learnMoreImg} alt={LOCALE.strings.learnMore} />
      </a>
      <div className={"lens-loader" + (isApplyingLens ? " visible" : " hidden")}>
          <div className="dot-pulse "></div>
      </div>
      {/* <PoweredBy /> */}
      {sharedBlob && <SharingScreen sharedBlob={sharedBlob}
                                    close={() => setSharedBlob(null)}
                                    showDebug={showDebug}
                                    width={width}
                                    height={height} />}
  </>);
}

export default AppUI;
